import * as React from "react";
import { toast, type ExternalToast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ErrorIcon,
  InfoIcon,
  LoadingIcon,
  SuccessIcon,
  WarningIcon,
} from "./toast-icons";

/**
 * Brand toast API — Light Theme Editorial Design
 *
 * The imperative toast surface (`brandToast`, `transactionToast`). It lives
 * apart from `BrandToastProvider` so that the provider's file exports only a
 * component (Fast Refresh) and this one exports only plain objects. Reach it
 * through `@/components/brand` or `@/hooks/useToast`.
 *
 * - Success: Light green with emerald text
 * - Error: Light red with red text
 * - Warning: Light amber with amber text
 * - Info: Light teal with teal text
 */

interface BrandToastOptions extends ExternalToast {
  icon?: React.ReactNode;
}

export const brandToast = {
  /**
   * Success toast
   */
  success: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <SuccessIcon />,
      className: cn(
        "bg-success/10 border-success/30",
        "text-success",
        options?.className,
      ),
    });
  },

  /**
   * Error toast
   */
  error: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <ErrorIcon />,
      className: cn(
        "bg-red-50 border-red-200",
        "text-red-900",
        options?.className,
      ),
    });
  },

  /**
   * Warning toast
   */
  warning: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <WarningIcon />,
      className: cn(
        "bg-amber-50 border-amber-200",
        "text-amber-900",
        options?.className,
      ),
    });
  },

  /**
   * Info toast
   */
  info: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <InfoIcon />,
      className: cn(
        "bg-brand-blue-50 border-brand-blue-200",
        "text-brand-blue-800",
        options?.className,
      ),
    });
  },

  /**
   * Loading toast
   */
  loading: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <LoadingIcon />,
      className: cn(
        "bg-card border-border",
        "text-foreground",
        options?.className,
      ),
      duration: Infinity,
    });
  },

  /**
   * Promise toast - shows loading, then success/error
   */
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
    options?: BrandToastOptions,
  ) => {
    return toast.promise(promise, {
      loading: msgs.loading,
      success: msgs.success,
      error: msgs.error,
      ...options,
    });
  },

  /**
   * Custom toast with full control
   */
  custom: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      className: cn(
        "bg-card border-border",
        "text-foreground",
        options?.className,
      ),
    });
  },

  /**
   * Dismiss a toast
   */
  dismiss: (toastId?: string | number) => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: () => {
    toast.dismiss();
  },
};

/**
 * Transaction Toast - specialized for blockchain transactions
 */
export interface TransactionToastOptions extends BrandToastOptions {
  txHash?: string;
  explorerUrl?: string;
}

export const transactionToast = {
  /**
   * Transaction pending
   */
  pending: (message: string, options?: TransactionToastOptions) => {
    return brandToast.loading(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
    });
  },

  /**
   * Transaction success
   */
  success: (message: string, options?: TransactionToastOptions) => {
    return brandToast.success(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
    });
  },

  /**
   * Transaction failed
   */
  error: (message: string, options?: TransactionToastOptions) => {
    return brandToast.error(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
    });
  },
};
